import { FC, useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import {
  DsmButton,
  DsmGrid,
  DsmIcon,
} from "@dsm-dcs/design-system-react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import {
  processAndStageStylesV2,
} from "../../../../../../_metronic/layout";
import {
  ShrimpFacilityOutputNames,
  ShrimpFacilityOutputs,
} from "../../../../models/Facility/Processing";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import {
  ShrimpBaseline,
  ShrimpProcessingOutputItem,
} from "../../../../models/Baseline/ShrimpBaseline";
import { FormType } from "../../common";
import {
  FacilityTableItem,
} from "../../../../models/Facility/FacilityTypes";
import ShrimpOutputFormOutputComponent from "./ShrimpOutputFormOuputComponent";

const DEFAULT_OUTPUT = {
  coolingType: undefined,
  outputName: "",
  weight: "",
  price: "",
  calculate: false,
  outputId: "",
};

interface ShrimpOutputFormContentProps {
  fieldItemPrefix: string;
  fieldName: ShrimpFacilityOutputNames;
  labelName: ShrimpFacilityOutputs;
  formType: FormType;
  facility: FacilityTableItem | undefined;
}

const ShrimpOutputFormContent: FC<ShrimpOutputFormContentProps> = ({
  fieldItemPrefix,
  fieldName,
  formType = FormType.Add,
  labelName,
  facility,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const formContext = useFormContext<ShrimpBaseline>();

  const {
    fields: outputs,
    append: appendOutput,
    remove: removeOutput,
  } = useFieldArray({
    name: `${fieldItemPrefix}.${fieldName}`,
    control: formContext.control,
    keyName: "keyId",
  });

  const addNewOutput = () => {
    const newOutput = { ...DEFAULT_OUTPUT };
    if (fieldName === ShrimpFacilityOutputNames.FRESH_MEAT_AND_EDIBLE_OFFAL) {
      newOutput.calculate = true;
      const outputLabel = intl.formatMessage({
        id: `BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.${labelName}`,
      });
      newOutput.outputName = `${outputLabel} ${outputs.length + 1}`;
    }
    newOutput.outputId = uuidv4();
    appendOutput(newOutput);
  };

  useEffect(() => {
    if (outputs.length === 0) {
      addNewOutput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  return (
    <div>
      <p
        className={classes.headingTitleVariant}
        style={{ marginTop: "var(--dsm-spacing-px-4)" }}
      >
        {intl.formatMessage({
          id: `BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.${labelName}`,
        })}
      </p>
      <DsmGrid className={classes.dsmGridOneColumn}>
        {outputs?.map((output, index) => (
          <ShrimpOutputFormOutputComponent
            key={output.keyId}
            fieldItemPrefix={fieldItemPrefix}
            fieldName={fieldName}
            formType={formType}
            facility={facility}
            index={index}
            output={output as ShrimpProcessingOutputItem} 
            removeOutput={removeOutput}
            isEdibleShrimp={labelName === ShrimpFacilityOutputs.freshMeatAndEdibleOffal}
          />
        ))}
        <DsmButton
          variant="text"
          onClick={addNewOutput}
          disabled={formType === FormType.View}
          style={{
            width: "max-content"
          }}
        >
          <DsmIcon slot="before" name="general/plus-circle" />
          {intl.formatMessage({
            id: "BASELINE.FORM.PROCESSING.SHRIMP.OUTPUTS.ADD_ANOTHER_OUTPUT",
          })}
        </DsmButton>
      </DsmGrid>
    </div>
  );
};

export default ShrimpOutputFormContent;
